import React, { useState, useEffect, useRef } from "react";
import { Calendar } from "lucide-react";
import dayjs from "dayjs";
import { DateRange } from "react-date-range";

// A dashboard status box can deep-link to an orders tab with the date range it
// was showing; pass it as initialDateRange so the list covers the same dates.
// Pages that don't pass it keep the "last 30 days" default.
const DateFilter = ({ onDateChange, clearTrigger, noInitialFilter, className, initialDateRange }) => {
    const dateRef = useRef(null);
    const [showCustom, setShowCustom] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const dateButtonRef = useRef(null);
    const calendarRef = useRef(null);
    const [popupPosition, setPopupPosition] = useState("left-0");

    const DEFAULT_DATE_RANGE = [
        {
            startDate: dayjs().subtract(29, "day").startOf("day").toDate(),
            endDate: dayjs().endOf("day").toDate(),
            key: "selection",
        },
    ];


    const startingRange = initialDateRange || DEFAULT_DATE_RANGE;

    const [dateRange, setDateRange] = useState(startingRange);

    const [tempDateRange, setTempDateRange] = useState(startingRange);

    // "Clear All Filters" goes back to the normal default, not the deep link.
    // Skipped on mount: the state is already seeded from startingRange.
    const isFirstClearRun = useRef(true);
    useEffect(() => {
        if (isFirstClearRun.current) {
            isFirstClearRun.current = false;
            return;
        }
        setDateRange(DEFAULT_DATE_RANGE);
        setTempDateRange(DEFAULT_DATE_RANGE);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clearTrigger]);

    // ✅ Dynamic Positioning for Custom Calendar
    React.useLayoutEffect(() => {
        if (showCustom && dateRef.current) {
            const rect = dateRef.current.getBoundingClientRect();
            const dropdownWidth = 350; // Approximate approx width of the calendar
            const spaceRight = window.innerWidth - rect.left;

            if (spaceRight < dropdownWidth) {
                setPopupPosition("right-0");
            } else {
                setPopupPosition("left-0");
            }
        }
    }, [showCustom]);

    const dateOptions = [
        {
            label: "Today",
            range: () => ({
                startDate: dayjs().startOf("day").toDate(),
                endDate: dayjs().endOf("day").toDate(),
            }),
        },
        {
            label: "Yesterday",
            range: () => ({
                startDate: dayjs().subtract(1, "day").startOf("day").toDate(),
                endDate: dayjs().subtract(1, "day").endOf("day").toDate(),
            }),
        },
        {
            label: "Last 7 Days",
            range: () => ({
                startDate: dayjs().subtract(29, "day").startOf("day").toDate(),
                endDate: dayjs().endOf("day").toDate(),
            }),
        },
        {
            label: "Last 30 Days",
            range: () => ({
                startDate: dayjs().subtract(29, "day").startOf("day").toDate(),
                endDate: dayjs().endOf("day").toDate(),
            }),
        },
        {
            label: "This Month",
            range: () => ({
                startDate: dayjs().startOf("month").toDate(),
                endDate: dayjs().endOf("day").toDate(),
            }),
        },
        {
            label: "Last Month",
            range: () => ({
                startDate: dayjs().subtract(1, "month").startOf("month").toDate(),
                endDate: dayjs().subtract(1, "month").endOf("month").toDate(),
            }),
        },
        {
            label: "This Year",
            range: () => ({
                startDate: dayjs().startOf("year").toDate(),
                endDate: dayjs().endOf("day").toDate(),
            }),
        },
        { label: "Custom", custom: true },
    ];

    useEffect(() => {
        if (!noInitialFilter) {
            onDateChange && onDateChange(startingRange);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // ✅ Handle outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dateRef.current && !dateRef.current.contains(event.target)) {
                setShowDropdown(false);
                setShowCustom(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleDateOptionClick = (option) => {
        if (option.custom) {
            // A filter that starts empty opens on today (with no dates the calendar paints the whole month as selected)
            setTempDateRange(
                dateRange?.[0]?.startDate
                    ? [...dateRange]
                    : [{ startDate: new Date(), endDate: new Date(), key: "selection" }]
            );
            setShowDropdown(false);
            requestAnimationFrame(() => setShowCustom(true));
        } else {
            const range = option.range();
            const newRange = [
                {
                    startDate: new Date(range.startDate),
                    endDate: new Date(range.endDate),
                    key: "selection",
                },
            ];
            setDateRange(newRange);
            setShowDropdown(false);
            setShowCustom(false);
            onDateChange && onDateChange(newRange); // ✅ pass to parent
        }
    };

    return (
        <div className="w-full">
            <div className={`relative w-full ${className || "sm:w-[200px]"}`} ref={dateRef}>
                {/* Button */}
                <button
                    className={`w-full bg-white py-2 px-3 sm:text-[12px] text-[10px] font-[600] border rounded-lg focus:outline-none text-left flex items-center justify-between text-gray-400 ${showDropdown ? "border-brand-primary" : "border-gray-300"}`}
                    onClick={() => {
                        if (showCustom) {
                            setShowDropdown(false);
                            setShowCustom(false);
                        } else {
                            setShowDropdown((prev) => !prev);
                            setShowCustom(false);
                        }
                    }}
                    ref={dateButtonRef}
                >
                    <span>
                        {dateRange?.[0]?.startDate && dateRange?.[0]?.endDate
                            ? `${dayjs(dateRange[0].startDate).format("DD/MM/YYYY")} - ${dayjs(
                                dateRange[0].endDate
                            ).format("DD/MM/YYYY")}`
                            : "Select Date"}

                    </span>
                    <Calendar
                        className={`w-4 h-4 ml-2 transition-colors ${showDropdown || showCustom ? "text-brand-primary" : "text-gray-400"}`}
                    />
                </button>

                {/* Dropdown */}
                {showDropdown && (
                    <div className="absolute w-full bg-white border rounded-lg shadow mt-1 z-[50] animate-popup-in">
                        <ul>
                            {dateOptions.map((option, idx) => (
                                <li
                                    key={idx}
                                    className="cursor-pointer hover:bg-brand-secondary/16 px-3 py-2 sm:text-[12px] text-[10px] font-[600] text-gray-500"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleDateOptionClick(option);
                                    }}
                                >
                                    {option.label}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Custom Calendar */}
                {showCustom && (
                    <div
                        className={`absolute top-[110%] bg-white border rounded shadow-sm p-3 z-[100] ${popupPosition}`}
                        ref={calendarRef}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <DateRange
                            editableDateInputs={true}
                            onChange={(item) => setTempDateRange([item.selection])}
                            ranges={tempDateRange}
                            moveRangeOnFirstSelection={false}
                            showMonthAndYearPickers={false}
                            rangeColors={["var(--brand-primary, #0CBB7D)"]}
                            months={1}
                            direction="horizontal"
                            showDateDisplay={false}
                            className="custom-date-range text-[12px] font-[600] text-gray-500"
                        />

                        <div className="flex justify-end gap-2 mt-2">
                            <button
                                className="bg-brand-primary text-white px-3 py-1 text-xs rounded"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    // react-date-range returns midnight for both ends, so a
                                    // single day is a zero-width window; stretch to full days.
                                    const normalizedRange = [
                                        {
                                            ...tempDateRange[0],
                                            startDate: dayjs(tempDateRange[0].startDate).startOf("day").toDate(),
                                            endDate: dayjs(tempDateRange[0].endDate).endOf("day").toDate(),
                                        },
                                    ];
                                    setDateRange(normalizedRange);
                                    setShowCustom(false);
                                    setShowDropdown(false);
                                    onDateChange && onDateChange(normalizedRange); // ✅ send to parent
                                }}
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DateFilter;

